import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { formatGuideTime } from '../../utils/guided'
import type { GuideMineItem } from '../../types/gotypes'

export interface GuidedResumeBoxProps {
  /** The person's guided conversations that can still go on (resumableGuides). */
  items: GuideMineItem[]
  onOpen: (responseId: string) => void
}

/**
 * The way back into a guided conversation from History (Matt, 29 Oct 2026:
 * "a little blue box in the agent history at the top"). Guided chats are
 * kept out of the chat list, so without it the only ways back are the bell
 * and the link. One row per conversation that is invited or in progress on
 * an open guide; nothing at all when there is none.
 */
const GuidedResumeBox: React.FC<GuidedResumeBoxProps> = ({ items, onOpen }) => {
  if (items.length === 0) return null
  return (
    <Box data-testid="guided-resume-box" sx={{ px: 1.5, pt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {items.map(({ response, guide_title }) => {
        const started = response.status === 'in_progress'
        const lastActive = formatGuideTime(response.last_active_at)
        return (
          <Box
            key={response.id}
            sx={(theme) => ({
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: alpha(theme.palette.info.main, 0.35),
              backgroundColor: alpha(theme.palette.info.main, 0.08),
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            })}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{guide_title}</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4 }}>
              {started
                ? `In progress${lastActive ? `, last active ${lastActive}` : ''}. Carry on where you left off.`
                : 'You are invited to a guided conversation. Start when you are ready.'}
              {' '}Stop any time and come back here.
            </Typography>
            <Box>
              <Button size="small" variant="contained" color="info" disableElevation onClick={() => onOpen(response.id)}>
                {started ? 'Carry on' : 'Start'}
              </Button>
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default GuidedResumeBox
